'use client';

import { alpha, Box, Typography } from '@mui/material';

import { Avatar, ComponentErrorBoundary } from '@/components';
import type { SearchProfile } from '@/types';

interface SearchProfileCardProps {
  profile: SearchProfile;
  onClick?: (profile: SearchProfile) => void;
  variant?: 'page' | 'dropdown';
}

export function SearchProfileCard({ profile, onClick, variant = 'page' }: SearchProfileCardProps) {
  const isPage = variant === 'page';

  return (
    <ComponentErrorBoundary componentName="SearchProfileCard">
      <Box
        onClick={() => onClick?.(profile)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          p: isPage ? 2 : 1.25,
          bgcolor: (theme) =>
            theme.palette.mode === 'dark'
              ? alpha(theme.palette.background.paper, 0.6)
              : theme.palette.background.paper,
          borderRadius: 2.5,
          border: '1px solid',
          borderColor: (theme) =>
            theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.08) : 'divider',
          cursor: onClick ? 'pointer' : 'default',
          transition: (theme) =>
            theme.transitions.create(
              ['background-color', 'transform', 'box-shadow', 'border-color'],
              {
                duration: theme.transitions.duration.shorter,
                easing: theme.transitions.easing.easeInOut,
              }
            ),
          '&:hover': onClick
            ? {
                bgcolor: (theme) =>
                  theme.palette.mode === 'dark'
                    ? alpha(theme.palette.primary.main, 0.08)
                    : alpha(theme.palette.primary.main, 0.04),
                transform: 'translateY(-1px)',
                boxShadow: (theme) =>
                  theme.palette.mode === 'dark'
                    ? '0 4px 12px rgba(0,0,0,0.4)'
                    : '0 4px 12px rgba(0,0,0,0.08)',
                borderColor: (theme) => alpha(theme.palette.primary.main, 0.3),
              }
            : {},
          mb: 1,
        }}
      >
        <Avatar
          src={profile?.profileImage}
          alt={profile?.name}
          size={isPage ? 44 : 32}
          sx={{
            boxShadow: (theme) =>
              `0 0 0 2px ${
                theme.palette.mode === 'dark'
                  ? alpha(theme.palette.common.white, 0.05)
                  : alpha(theme.palette.common.black, 0.05)
              }`,
          }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant={isPage ? 'h6' : 'body1'}
            sx={{
              fontWeight: 800,
              color: 'text.primary',
              fontSize: isPage ? '1.1rem' : '0.95rem',
              letterSpacing: '-0.01em',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {profile?.name}
          </Typography>
          {profile.pseudonym && !isPage && (
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                display: 'block',
                fontWeight: 600,
                opacity: 0.6,
                letterSpacing: '0.01em',
              }}
            >
              @{profile?.pseudonym}
            </Typography>
          )}
          {isPage && profile?.pseudonym && (
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                display: 'block',
                fontWeight: 500,
                opacity: 0.7,
              }}
            >
              @{profile.pseudonym}
            </Typography>
          )}
        </Box>
      </Box>
    </ComponentErrorBoundary>
  );
}
